/**
 * AdminBugReports.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import LoadingSpinner from './LoadingSpinner';
import './AdminBugReports.css';

const API_URL = '/api/bug-report';

/** Workflow order for status sort (open → pending → followup → closed). */
const STATUS_ORDER = { open: 0, pending: 1, followup: 2, closed: 3 };

/** Avoid stale lists: server used to set max-age on GET /api/*; always bypass cache for admin bug APIs. */
const noStore = { cache: 'no-store' };

const AdminBugReports = () => {
  const { currentUser, hasAnyRole, hasAnyPermission } = useAuth();
  const { showSuccess, showError } = useToast();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [pendingReportId, setPendingReportId] = useState(null);
  const [comment, setComment] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [sortBy, setSortBy] = useState('age-oldest'); // age-oldest, age-newest, updated-oldest, updated-newest, title-asc, title-desc, status
  const [statusCounts, setStatusCounts] = useState({ open: 0, pending: 0, followup: 0, closed: 0 });
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('skip');
  const importFileInputRef = useRef(null);

  const totalCount = useMemo(
    () => Object.values(statusCounts).reduce((sum, n) => sum + n, 0),
    [statusCounts]
  );

  // Match backend requireAdmin + ProtectedAdminRoute: role names may be strings or { name } objects
  const canManageBugReports =
    currentUser &&
    hasAnyRole &&
    (hasAnyRole(['admin', 'super_admin']) ||
      (typeof hasAnyPermission === 'function' &&
        hasAnyPermission(['*', 'bug_reports.admin'])));

  // Fetch all bug reports
  const fetchReports = useCallback(async (opts = {}) => {
    const { skipLoading = false } = opts;
    try {
      if (!skipLoading) setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Not authenticated. Please log in first.');
      }

      const qs = new URLSearchParams();
      qs.set('limit', 'all');
      qs.set('_t', String(Date.now()));
      const response = await fetch(`${API_URL}/admin?${qs.toString()}`, {
        ...noStore,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });

      if (!response.ok) {
        let msg = `Failed to fetch bug reports (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData.error) msg = errorData.error;
        } catch {
          /* non-JSON body */
        }
        throw new Error(msg);
      }

      const data = await response.json();
      setReports(data.reports || []);
      if (data.statusCounts) {
        setStatusCounts(data.statusCounts);
      } else {
        const counts = { open: 0, pending: 0, followup: 0, closed: 0 };
        (data.reports || []).forEach((r) => {
          if (Object.prototype.hasOwnProperty.call(counts, r.status)) counts[r.status] += 1;
        });
        setStatusCounts(counts);
      }
      setFilteredReports(data.reports || []);
    } catch (err) {
      console.error('Fetch bug reports error:', err);
      showError(err.message);
    } finally {
      if (!skipLoading) setLoading(false);
    }
  }, [showError]);

  const fetchAssignees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/assignees?_t=${Date.now()}`, {
        ...noStore,
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });
      if (response.ok) {
        setAssignees(await response.json());
      }
    } catch (err) {
      console.error('Fetch assignees error:', err);
    }
  };

  useEffect(() => {
    if (canManageBugReports) {
      fetchReports();
      fetchAssignees();
    }
  }, [canManageBugReports, fetchReports]);

  // Filter and sort reports (status filter is client-side so counts stay accurate)
  useEffect(() => {
    let result = [...reports];

    if (statusFilter !== 'all') {
      result = result.filter((report) => report.status === statusFilter);
    }

    // Filter by search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(report => 
        report.issue_title.toLowerCase().includes(search) ||
        report.issue_description.toLowerCase().includes(search) ||
        (report.user_email && report.user_email.toLowerCase().includes(search)) ||
        (report.current_url && report.current_url.toLowerCase().includes(search))
      );
    }

    // Sort the result
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'newest' || sortBy === 'age-newest') {
        comparison = new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'oldest' || sortBy === 'age-oldest') {
        comparison = new Date(a.created_at) - new Date(b.created_at);
      } else if (sortBy === 'updated-newest') {
        comparison = new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      } else if (sortBy === 'updated-oldest') {
        comparison = new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
      } else if (sortBy === 'title-asc') {
        comparison = a.issue_title.localeCompare(b.issue_title);
      } else if (sortBy === 'title-desc') {
        comparison = b.issue_title.localeCompare(a.issue_title);
      } else if (sortBy === 'status') {
        const aOrder = STATUS_ORDER[a.status] ?? 99;
        const bOrder = STATUS_ORDER[b.status] ?? 99;
        comparison = aOrder !== bOrder ? aOrder - bOrder : a.issue_title.localeCompare(b.issue_title);
      }
      return comparison;
    });

    setFilteredReports(result);
  }, [searchTerm, statusFilter, reports, sortBy]);

  // Handle status change
  const handleStatusChange = async (reportId, newStatus) => {
    // ALWAYS show comment modal to track history accurately
    setPendingReportId(reportId);
    setPendingStatusChange(newStatus);
    setShowCommentModal(true);
  };

  // Perform the actual status change with optional comment — returns true on success (caller may close modal)
  const performStatusChange = async (reportId, newStatus, commentText) => {
    const idNum = Number(reportId);
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/${idNum}/status`, {
        ...noStore,
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus,
          comment: commentText || undefined
        })
      });

      if (!response.ok) {
        let msg = `Failed to update status (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData.error) msg = errorData.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      
      const data = await response.json();

      showSuccess('Bug report status updated successfully');
      
      // Update local state (normalize id types from API vs form)
      setReports((prev) =>
        prev.map((report) =>
          Number(report.id) === idNum
            ? { ...report, status: newStatus, history_log: data.history_log }
            : report
        )
      );

      if (selectedReport && Number(selectedReport.id) === idNum) {
        setSelectedReport({ ...selectedReport, status: newStatus, history_log: data.history_log });
      }
      await fetchReports({ skipLoading: true });
      return true;
    } catch (err) {
      console.error('Update status error:', err);
      showError(err.message);
      return false;
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle assignment change
  const handleAssignChange = async (reportId, assigneeId) => {
    const idNum = Number(reportId);
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/${idNum}/assign`, {
        ...noStore,
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_to: assigneeId ? parseInt(assigneeId, 10) : null })
      });

      if (!response.ok) {
        let msg = `Failed to assign (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData.error) msg = errorData.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      const data = await response.json();
      showSuccess('Assigned successfully');

      setReports((prev) =>
        prev.map((report) =>
          Number(report.id) === idNum
            ? { ...report, assigned_to: data.assigned_to, history_log: data.history_log }
            : report
        )
      );

      if (selectedReport && Number(selectedReport.id) === idNum) {
        setSelectedReport({ ...selectedReport, assigned_to: data.assigned_to, history_log: data.history_log });
      }
      await fetchReports({ skipLoading: true });
    } catch (err) {
      console.error('Assign error:', err);
      showError(err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle comment modal confirmation
  const handleCommentConfirm = async () => {
    if (pendingReportId && pendingStatusChange) {
      if (pendingReportId === 'bulk') {
        try {
          setUpdatingStatus(true);
          const token = localStorage.getItem('token');

          const results = await Promise.all(
            Array.from(selectedReports).map((reportId) =>
              fetch(`${API_URL}/admin/${Number(reportId)}/status`, {
                ...noStore,
                method: 'PUT',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: pendingStatusChange, comment: comment })
              })
            )
          );

          const failed = results.filter((r) => !r.ok);
          if (failed.length > 0) {
            let detail = '';
            try {
              const j = await failed[0].clone().json();
              if (j.error) detail = j.error;
            } catch {
              /* ignore */
            }
            showError(detail || `Bulk update failed for ${failed.length} report(s). Check login / permissions.`);
            return;
          }

          showSuccess(`Successfully marked ${selectedReports.size} reports as ${pendingStatusChange}`);
          setSelectedReports(new Set());
          await fetchReports();
        } catch (err) {
          console.error('Bulk update error:', err);
          showError(err.message || 'Failed to update some reports');
          return;
        } finally {
          setUpdatingStatus(false);
        }
      } else {
        const ok = await performStatusChange(pendingReportId, pendingStatusChange, comment);
        if (!ok) return;
      }

      setShowCommentModal(false);
      setComment('');
      setPendingReportId(null);
      setPendingStatusChange(null);
    }
  };

  // Handle comment modal cancellation
  const handleCommentCancel = () => {
    setShowCommentModal(false);
    setComment('');
    setPendingReportId(null);
    setPendingStatusChange(null);
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-badge status-open';
      case 'pending':
        return 'status-badge status-pending';
      case 'followup':
        return 'status-badge status-followup';
      case 'closed':
        return 'status-badge status-closed';
      default:
        return 'status-badge';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return '🐛';
      case 'pending':
        return '⏳';
      case 'followup':
        return '🗣️';
      case 'closed':
        return '✅';
      default:
        return '📋';
    }
  };

  // Get issue type icon
  const getIssueTypeIcon = (type) => {
    switch (type) {
      case 'bug':
        return '🐛';
      case 'ui':
        return '🎨';
      case 'performance':
        return '⚡';
      case 'feature':
        return '💡';
      case 'other':
        return '📝';
      default:
        return '📋';
    }
  };

  // Format date (using Atlantic Time)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-CA', {
      timeZone: 'America/Halifax',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Relative age string: "2 hours ago", "3 days ago", etc.
  const formatAge = (dateString) => {
    if (!dateString) return { label: 'N/A', tier: 'age-unknown' };
    const diffMs = Date.now() - new Date(dateString).getTime();
    const mins   = Math.floor(diffMs / 60000);
    const hours  = Math.floor(diffMs / 3600000);
    const days   = Math.floor(diffMs / 86400000);
    const weeks  = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    let label;
    if (mins < 60)        label = mins <= 1 ? 'Just now' : `${mins}m ago`;
    else if (hours < 24)  label = `${hours}h ago`;
    else if (days < 7)    label = `${days}d ago`;
    else if (weeks < 5)   label = `${weeks}w ago`;
    else                  label = `${months}mo ago`;

    // Colour tier: green < 1 day, yellow 1–3 days, orange 4–14 days, red > 14 days
    let tier;
    if (days < 1)        tier = 'age-fresh';
    else if (days < 4)   tier = 'age-recent';
    else if (days < 15)  tier = 'age-aging';
    else                 tier = 'age-old';

    return { label, tier };
  };

  // View report details
  const viewReportDetails = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/${Number(reportId)}?_t=${Date.now()}`, {
        ...noStore,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report details');
      }

      const report = await response.json();
      setSelectedReport(report);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Fetch report details error:', err);
      showError(err.message);
    }
  };

  // Handle delete report
  const handleDeleteReport = () => {
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDeleteReport = async () => {
    if (!selectedReport) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');

      const idNum = Number(selectedReport.id);
      const response = await fetch(`${API_URL}/admin/${idNum}`, {
        ...noStore,
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let msg = `Failed to delete (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData.error) msg = errorData.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      showSuccess('Bug report deleted successfully');
      
      // Remove from local state
      setReports((prev) => prev.filter((r) => Number(r.id) !== idNum));
      setFilteredReports((prev) => prev.filter((r) => Number(r.id) !== idNum));
      
      // Close modals
      setShowDeleteConfirm(false);
      setShowDetailsModal(false);
      setSelectedReport(null);
      await fetchReports({ skipLoading: true });
    } catch (err) {
      console.error('Delete bug report error:', err);
      showError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Bulk Actions
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedReports.size === 0) return;
    setPendingReportId('bulk');
    setPendingStatusChange(newStatus);
    setShowCommentModal(true);
  };

  const handleBulkDelete = async () => {
    if (selectedReports.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedReports.size} reports? This cannot be undone.`)) return;
    
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      const results = await Promise.all(
        Array.from(selectedReports).map((reportId) =>
          fetch(`${API_URL}/admin/${Number(reportId)}`, {
            ...noStore,
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        )
      );

      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        let detail = '';
        try {
          const j = await failed[0].clone().json();
          if (j.error) detail = j.error;
        } catch {
          /* ignore */
        }
        showError(detail || `Delete failed for ${failed.length} report(s).`);
        return;
      }

      showSuccess(`Successfully deleted ${selectedReports.size} reports`);
      setSelectedReports(new Set());
      await fetchReports({ skipLoading: true });
    } catch (err) {
      console.error('Bulk delete error:', err);
      showError('Failed to delete some reports');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedReports.size === filteredReports.length && filteredReports.length > 0) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(filteredReports.map(r => r.id)));
    }
  };

  const toggleSelectReport = (e, reportId) => {
    e.stopPropagation();
    const newSet = new Set(selectedReports);
    if (newSet.has(reportId)) {
      newSet.delete(reportId);
    } else {
      newSet.add(reportId);
    }
    setSelectedReports(newSet);
  };

  const handleExport = async (scope = 'selected') => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      if (scope === 'all') {
        const response = await fetch(`${API_URL}/admin/export?_t=${Date.now()}`, {
          ...noStore,
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`Export failed (${response.status})`);
        }
        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : `bug_reports_${new Date().toISOString().slice(0, 10)}.json`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        showSuccess(`Exported ${totalCount} bug report(s)`);
        return;
      }

      if (scope === 'selected') {
        const selectedList = reports.filter((r) => selectedReports.has(r.id));
        if (selectedList.length === 0) {
          showError('Select one or more bug reports to export');
          return;
        }
        const payload = {
          version: 1,
          exportedAt: new Date().toISOString(),
          source: 'cranford-bug-reports',
          count: selectedList.length,
          reports: selectedList
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bug_reports_selected_${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        showSuccess(`Exported ${selectedList.length} bug report(s)`);
      }
    } catch (err) {
      console.error('Export bug reports error:', err);
      showError(err.message || 'Failed to export bug reports');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleImportFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      const reportsToImport = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed.reports)
          ? parsed.reports
          : null;

      if (!reportsToImport || reportsToImport.length === 0) {
        throw new Error('File must contain a reports array');
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/import`, {
        ...noStore,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reports: reportsToImport, onDuplicate: importMode })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Import failed (${response.status})`);
      }

      showSuccess(result.message || 'Bug reports imported successfully');
      setShowImportModal(false);
      await fetchReports();
    } catch (err) {
      console.error('Import bug reports error:', err);
      showError(err.message || 'Failed to import bug reports');
    } finally {
      setImporting(false);
    }
  };

  const triggerImportFilePicker = () => {
    importFileInputRef.current?.click();
  };

  if (!canManageBugReports) {
    return (
      <div className="admin-bug-reports-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to view bug reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-bug-reports-container">
      <div className="admin-bug-reports-header">
        <h2>
          <span className="header-icon">🐛</span>
          Bug Reports
        </h2>
        <div className="bug-reports-io-toolbar">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handleExport('selected')}
            disabled={exporting || loading || selectedReports.size === 0}
            title="Export checked bug reports as JSON"
          >
            {exporting ? 'Exporting…' : '⬇ Export Selected'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handleExport('all')}
            disabled={exporting || loading}
            title="Export every bug report regardless of filters"
          >
            Export All
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleImportClick}
            disabled={importing || loading}
          >
            {importing ? 'Importing…' : '⬆ Import'}
          </button>
          <input
            ref={importFileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={handleImportFileChosen}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bug-reports-filters">
        <div className="bug-reports-filters-row">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by title, description, email, or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="sort-box">
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <optgroup label="Age (submitted)">
              <option value="age-oldest">⏳ Oldest First (most urgent)</option>
              <option value="age-newest">🆕 Newest First</option>
            </optgroup>
            <optgroup label="Last Updated">
              <option value="updated-oldest">🕰️ Least Recently Updated</option>
              <option value="updated-newest">🔄 Most Recently Updated</option>
            </optgroup>
            <optgroup label="Other">
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-desc">Title (Z-A)</option>
              <option value="status">Status (open → closed)</option>
            </optgroup>
          </select>
        </div>
        </div>

      <div className="status-filter-buttons">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({totalCount})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'open' ? 'active' : ''}`}
            onClick={() => setStatusFilter('open')}
          >
            Open ({statusCounts.open})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending ({statusCounts.pending})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'followup' ? 'active' : ''}`}
            onClick={() => setStatusFilter('followup')}
          >
            Follow Up ({statusCounts.followup})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'closed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('closed')}
          >
            Closed ({statusCounts.closed})
          </button>
        </div>
      </div>

      {/* Bug Reports List */}
      {loading ? (
        <LoadingSpinner message="Loading bug reports..." />
      ) : filteredReports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🐛</div>
          <h3>No bug reports found</h3>
          <p>
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No bug reports have been submitted yet'}
          </p>
        </div>
      ) : (
        <div className="bug-reports-list-container">
          <div className="bulk-actions-toolbar">
            <label>
              <input 
                type="checkbox" 
                checked={filteredReports.length > 0 && selectedReports.size === filteredReports.length}
                onChange={toggleSelectAll}
              />
              Select All
            </label>
            
            {selectedReports.size > 0 && (
              <div className="bulk-actions-selected">
                <span className="bulk-actions-selected-count">
                  {selectedReports.size} selected
                </span>
                <button className="btn-secondary" onClick={() => handleBulkStatusChange('open')} disabled={updatingStatus}>Mark Open</button>
                <button className="btn-secondary" onClick={() => handleBulkStatusChange('pending')} disabled={updatingStatus}>Mark Pending</button>
                <button className="btn-secondary" onClick={() => handleBulkStatusChange('followup')} disabled={updatingStatus}>Mark Follow Up</button>
                <button className="btn-secondary" onClick={() => handleBulkStatusChange('closed')} disabled={updatingStatus}>Mark Closed</button>
                <button className="btn-delete" onClick={handleBulkDelete} disabled={deleting}>Delete</button>
              </div>
            )}
          </div>
          <div className="bug-reports-list">
          {filteredReports.map((report) => (
            <div key={report.id} className={`bug-report-card ${selectedReports.has(report.id) ? 'selected' : ''}`}>
              <div className="report-header">
                <input 
                  type="checkbox" 
                  checked={selectedReports.has(report.id)}
                  onChange={(e) => toggleSelectReport(e, report.id)}
                />
                <div className="report-title-section">
                  <span className="issue-type-icon">{getIssueTypeIcon(report.issue_type)}</span>
                  <h3 className="report-title" onClick={() => viewReportDetails(report.id)} title={report.issue_title}>
                    {report.issue_title}
                  </h3>
                </div>
                <span className={getStatusBadgeClass(report.status)}>
                  {getStatusIcon(report.status)} {report.status}
                </span>
              </div>

              <div className="report-body">
                <p className="report-description" title={report.issue_description}>
                  {report.issue_description}
                </p>
              </div>

              <div className="report-footer">
                <div className="report-meta">
                  <span className="meta-item">
                    <strong>Type:</strong> {report.issue_type}
                  </span>
                  {report.user_email && (
                    <span className="meta-item">
                      <strong>Email:</strong> {report.user_email}
                    </span>
                  )}
                  {report.current_url && (
                    <span className="meta-item meta-item-wrap">
                      <strong>URL:</strong>{' '}
                      <a href={report.current_url} target="_blank" rel="noopener noreferrer">
                        {report.current_url.length > 40
                          ? `${report.current_url.substring(0, 40)}…`
                          : report.current_url}
                      </a>
                    </span>
                  )}
                  <span className="meta-item">
                    <strong>Submitted:</strong> {formatDate(report.created_at)}
                    {' '}
                    <span className={`age-badge ${formatAge(report.created_at).tier}`} title={`Submitted ${formatDate(report.created_at)}`}>
                      {formatAge(report.created_at).label}
                    </span>
                  </span>
                  {report.updated_at && report.updated_at !== report.created_at && (
                    <span className="meta-item">
                      <strong>Updated:</strong> {formatDate(report.updated_at)}
                      {' '}
                      <span className={`age-badge ${formatAge(report.updated_at).tier}`} title={`Updated ${formatDate(report.updated_at)}`}>
                        {formatAge(report.updated_at).label}
                      </span>
                    </span>
                  )}
                </div>

                <div className="report-actions">
                  <button
                    className="btn-view"
                    onClick={() => viewReportDetails(report.id)}
                  >
                    Details
                  </button>
                  <select
                    className="status-select"
                    value={report.status}
                    onChange={(e) => handleStatusChange(report.id, e.target.value)}
                    disabled={updatingStatus}
                    aria-label="Change status"
                  >
                    <option value="open">Open</option>
                    <option value="pending">Pending</option>
                    <option value="followup">Follow Up</option>
                    <option value="closed">Closed</option>
                  </select>
                  <select
                    className="status-select status-select-assign"
                    value={report.assigned_to || ''}
                    onChange={(e) => handleAssignChange(report.id, e.target.value)}
                    disabled={updatingStatus}
                    aria-label="Assign to"
                  >
                    <option value="">Unassigned</option>
                    {assignees.map(user => (
                      <option key={user.id} value={user.id}>{user.username || user.email}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <span className="issue-type-icon">{getIssueTypeIcon(selectedReport.issue_type)}</span>
                {selectedReport.issue_title}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowDetailsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Status</h3>
                <select
                  className="status-select-large"
                  value={selectedReport.status}
                  onChange={(e) => handleStatusChange(selectedReport.id, e.target.value)}
                  disabled={updatingStatus}
                >
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="followup">Follow Up</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="detail-section">
                <h3>Assignment</h3>
                <select
                  className="status-select-large"
                  value={selectedReport.assigned_to || ''}
                  onChange={(e) => handleAssignChange(selectedReport.id, e.target.value)}
                  disabled={updatingStatus}
                >
                  <option value="">Unassigned</option>
                  {assignees.map(user => (
                    <option key={user.id} value={user.id}>{user.username || user.email}</option>
                  ))}
                </select>
              </div>

              <div className="detail-section">
                <h3>Issue Description</h3>
                <p className="description-text">{selectedReport.issue_description}</p>
              </div>

              <div className="detail-section">
                <h3>Report Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Issue Type:</strong> {selectedReport.issue_type}
                  </div>
                  <div className="detail-item">
                    <strong>User Email:</strong>{' '}
                    {selectedReport.user_email || 'Not provided'}
                  </div>
                  <div className="detail-item">
                    <strong>Current URL:</strong>{' '}
                    {selectedReport.current_url ? (
                      <a href={selectedReport.current_url} target="_blank" rel="noopener noreferrer">
                        {selectedReport.current_url}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="detail-item">
                    <strong>Submitted:</strong> {formatDate(selectedReport.created_at)}
                  </div>
                  <div className="detail-item">
                    <strong>Last Updated:</strong> {formatDate(selectedReport.updated_at)}
                  </div>
                </div>
              </div>

              {selectedReport.history_log && selectedReport.history_log.length > 0 && (
                <div className="detail-section">
                  <h3>Audit History</h3>
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '15px' }}>
                    {selectedReport.history_log.map((entry, index) => (
                      <div key={index} style={{ marginBottom: index !== selectedReport.history_log.length - 1 ? '15px' : '0', paddingBottom: index !== selectedReport.history_log.length - 1 ? '15px' : '0', borderBottom: index !== selectedReport.history_log.length - 1 ? '1px solid #e9ecef' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9rem' }}>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>
                            {entry.adminName || 'Admin'} changed status: <span style={{ color: '#6b7280', textDecoration: 'line-through' }}>{entry.oldStatus}</span> ➔ <span style={{ color: '#10b981' }}>{entry.newStatus}</span>
                          </span>
                          <span style={{ color: '#6b7280' }}>
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                        {entry.comment && (
                          <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '6px', fontSize: '0.9rem', color: '#1e40af', fontStyle: 'italic' }}>
                            "{entry.comment}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedReport.browser_info && (
                <div className="detail-section">
                  <h3>Browser Information</h3>
                  <pre className="json-display">
                    {JSON.stringify(selectedReport.browser_info, null, 2)}
                  </pre>
                </div>
              )}

              {selectedReport.server_metadata && (
                <div className="detail-section">
                  <h3>Server Metadata</h3>
                  <pre className="json-display">
                    {JSON.stringify(selectedReport.server_metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedReport.console_logs && (
                <div className="detail-section">
                  <h3>Console Logs</h3>
                  <pre className="console-logs-display">{selectedReport.console_logs}</pre>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-delete"
                onClick={handleDeleteReport}
                disabled={deleting}
              >
                🗑️ Delete Report
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedReport && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗑️ Delete Bug Report</h2>
              <button
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to delete this bug report?</p>
              <div className="delete-confirm-details">
                <p><strong>Title:</strong> {selectedReport.issue_title}</p>
                <p><strong>Type:</strong> {selectedReport.issue_type}</p>
                <p><strong>Submitted:</strong> {formatDate(selectedReport.created_at)}</p>
              </div>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={confirmDeleteReport}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal for Status Action */}
      {showCommentModal && (
        <div className="modal-overlay" onClick={handleCommentCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{pendingReportId === 'bulk' ? '🔄 Bulk Update Status' : '🔄 Update Report Status'}</h2>
              <button
                className="modal-close"
                onClick={handleCommentCancel}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>You are about to change the status to <strong>{pendingStatusChange}</strong>. Would you like to add a comment? This comment is appended to the audit history and emailed to the original reporter.</p>
              <div className="detail-section" style={{ marginTop: '20px' }}>
                <label htmlFor="bug-comment" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  Comment / Administrator Note (optional):
                </label>
                <textarea
                  id="bug-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Explain why this status is being updated... (User gets an email)"
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                  {pendingReportId === 'bulk' 
                    ? `This text will be blanket applied to all ${selectedReports.size} selected reports.`
                    : (pendingReportId &&
                        reports.find((r) => Number(r.id) === Number(pendingReportId))?.user_email
                        ? `This note will be logged and instantly sent to the user via email.`
                        : 'No user email was provided on this bug, but the internal audit log will still update.')}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCommentCancel}
                disabled={updatingStatus}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCommentConfirm}
                disabled={updatingStatus}
                style={{ backgroundColor: '#3b82f6', color: 'white' }}
              >
                {updatingStatus ? 'Updating...' : `✅ Update to ${pendingStatusChange}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => !importing && setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⬆ Import Bug Reports</h2>
              <button
                className="modal-close"
                onClick={() => !importing && setShowImportModal(false)}
                disabled={importing}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Upload a JSON file exported from this admin page (or another environment).</p>
              <div className="detail-section">
                <label htmlFor="import-mode" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  When a report ID already exists:
                </label>
                <select
                  id="import-mode"
                  className="status-select-large"
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                  disabled={importing}
                >
                  <option value="skip">Skip duplicates</option>
                  <option value="update">Update existing records</option>
                  <option value="create">Always create new records (ignore IDs)</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowImportModal(false)}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={triggerImportFilePicker}
                disabled={importing}
              >
                {importing ? 'Importing…' : 'Choose JSON file…'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBugReports;

